<template>
  <PreAuthPage class="csp" card-class="csp__card">
    <section class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="48" />
      <h1 class="sh-card__title">{{ sent ? t("signup.sentTitle") : t("signup.title") }}</h1>
      <p class="sh-card__subtitle">
        {{ sent ? t("signup.sentBody", { email: sentTo }) : t("signup.subtitle", { name: tenantName }) }}
      </p>
    </section>

    <!-- Success: the next step happens in their inbox, on Auth0's set-password
         page (our branding), and then back here signed in. -->
    <template v-if="sent">
      <div class="csp__sent" role="status">
        <v-icon size="22" color="primary">mark_email_read</v-icon>
        <p>{{ t("signup.sentHint") }}</p>
      </div>
      <v-btn class="csp__back" color="primary" variant="tonal" block @click="goTo('/')">{{ t("signup.backToSignIn") }}</v-btn>
    </template>

    <form v-else class="csp__form" novalidate @submit.prevent="submit">
      <p v-if="error" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ error }}</span>
      </p>

      <h2 class="csp__section">{{ t("signup.companySection") }}</h2>
      <v-text-field
        v-model="legalName"
        :label="t('onboarding.company.legalName')"
        :hint="t('onboarding.company.legalNameHint')"
        persistent-hint
        variant="outlined"
        density="comfortable"
        autocomplete="organization"
        autofocus
        :disabled="busy"
      />
      <div class="csp__row">
        <v-text-field
          v-model="registrationNumber"
          :label="t('onboarding.company.registrationNumber')"
          variant="outlined"
          density="comfortable"
          autocomplete="off"
          :disabled="busy"
          hide-details
        />
        <v-text-field
          v-model="vatNumber"
          :label="t('onboarding.company.vatNumber')"
          variant="outlined"
          density="comfortable"
          autocomplete="off"
          :disabled="busy"
          hide-details
        />
      </div>
      <div class="csp__row">
        <v-autocomplete
          v-model="countryCode"
          :items="countries"
          item-title="title"
          item-value="value"
          :label="t('onboarding.company.country')"
          variant="outlined"
          density="comfortable"
          autocomplete="off"
          :disabled="busy"
          hide-details
        />
        <v-autocomplete
          v-model="baseCurrency"
          :items="currencies"
          item-title="title"
          item-value="value"
          :label="t('onboarding.company.baseCurrency')"
          variant="outlined"
          density="comfortable"
          autocomplete="off"
          :disabled="busy"
          hide-details
        />
      </div>

      <h2 class="csp__section">{{ t("signup.personSection") }}</h2>
      <div class="csp__row">
        <v-text-field
          v-model="firstName"
          :label="t('signup.firstName')"
          variant="outlined"
          density="comfortable"
          autocomplete="given-name"
          :disabled="busy"
          hide-details
        />
        <v-text-field
          v-model="lastName"
          :label="t('signup.lastName')"
          variant="outlined"
          density="comfortable"
          autocomplete="family-name"
          :disabled="busy"
          hide-details
        />
      </div>
      <v-text-field
        v-model="email"
        :label="t('signup.email')"
        :hint="t('signup.emailHint')"
        persistent-hint
        type="email"
        variant="outlined"
        density="comfortable"
        autocomplete="email"
        inputmode="email"
        :disabled="busy"
      />
      <v-text-field
        v-model="mobile"
        :label="t('signup.mobile')"
        type="tel"
        variant="outlined"
        density="comfortable"
        autocomplete="tel"
        inputmode="tel"
        :disabled="busy"
        hide-details
      />

      <!-- Honeypot: off-screen, out of the tab order, hidden from assistive
           tech. People never fill it; form-filling bots do. Its name and label
           mean nothing to browser autofill or password managers on purpose —
           a "website"-looking field gets autofilled, and the backend then
           silently drops a real visitor. Sent to the API as `website`. -->
      <div class="csp__trap" aria-hidden="true">
        <label>Leave blank <input v-model="trap" type="text" name="csp_hp_7" tabindex="-1" autocomplete="off" /></label>
      </div>

      <v-btn
        type="submit"
        color="primary"
        size="large"
        block
        class="csp__submit"
        :loading="busy"
        :disabled="!canSubmit"
        >{{ t("signup.submit") }}</v-btn
      >
      <p class="csp__foot">{{ t("signup.footNote") }}</p>
      <v-btn class="csp__switch" variant="text" size="small" block @click="goTo('/')">{{ t("signup.haveAccount") }}</v-btn>
    </form>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * PAC-1388 — the Packhouse Edition's own sign-up page, at /signup.
 *
 * Our design end to end: tenant branding, theme, i18n. No password field — the
 * backend emails an Auth0 set-password link (styled with our branding on
 * Auth0's side), so a password never touches our servers. Their first sign-in
 * creates the company from what was typed here (stores/session.ts →
 * completePendingSignupIfAny), then WMS opens site creation.
 *
 * Rendered by App.vue only on a front door that offers it (offersCompanySignup)
 * and only while signed out. The server answers the same "check your email"
 * whether or not the address already has an account, so this page does too.
 */
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import { countryOptions, currencyOptions } from "@/shell/signupOptions";
import { goTo, requestCompanySignup } from "@/signup/companySignup";
import { ApiRequestError } from "@/auth/session";

const MIN_LEGAL_NAME_LENGTH = 2;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HTTP_TOO_MANY = 429;

export default defineComponent({
  name: "CompanySignupPage",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t, locale } = useI18n();
    const session = useSessionStore();

    const legalName = ref("");
    const registrationNumber = ref("");
    const vatNumber = ref("");
    const countryCode = ref<string | null>(null);
    const baseCurrency = ref<string | null>(null);
    const firstName = ref("");
    const lastName = ref("");
    const email = ref("");
    const mobile = ref("");
    const trap = ref("");
    const busy = ref(false);
    const error = ref<string | null>(null);
    const sent = ref(false);
    const sentTo = ref("");

    const countries = computed(() => countryOptions(locale.value));
    const currencies = computed(() => currencyOptions(locale.value));

    const canSubmit = computed(
      () =>
        legalName.value.trim().length >= MIN_LEGAL_NAME_LENGTH &&
        !!countryCode.value &&
        !!baseCurrency.value &&
        !!firstName.value.trim() &&
        !!lastName.value.trim() &&
        EMAIL_SHAPE.test(email.value.trim()) &&
        !busy.value,
    );

    /** The server's messages are written for the visitor; keep them, minus the transport prefix. */
    function messageFor(e: unknown): string {
      if (e instanceof ApiRequestError && e.status === HTTP_TOO_MANY) return t("signup.tooMany");
      const raw = (e as Error)?.message?.replace(/^POST \S+ failed: \d+\s*/, "").trim();
      return raw || t("signup.failed");
    }

    async function submit(): Promise<void> {
      if (!canSubmit.value || !session.tenant?.slug) return;
      busy.value = true;
      error.value = null;
      const address = email.value.trim();
      try {
        await requestCompanySignup({
          tenantAppSlug: session.tenant.slug,
          person: {
            firstName: firstName.value.trim(),
            lastName: lastName.value.trim(),
            email: address,
            mobile: mobile.value.trim() || undefined,
          },
          company: {
            legalName: legalName.value.trim(),
            registrationNumber: registrationNumber.value.trim() || undefined,
            vatNumber: vatNumber.value.trim() || undefined,
            countryCode: countryCode.value as string,
            baseCurrency: baseCurrency.value as string,
          },
          website: trap.value || undefined,
        });
        sentTo.value = address;
        sent.value = true;
      } catch (e) {
        error.value = messageFor(e);
      } finally {
        busy.value = false;
      }
    }

    return {
      t,
      legalName,
      registrationNumber,
      vatNumber,
      countryCode,
      baseCurrency,
      firstName,
      lastName,
      email,
      mobile,
      trap,
      countries,
      currencies,
      busy,
      error,
      sent,
      sentTo,
      canSubmit,
      submit,
      goTo,
      tenantName: computed(() => session.tenant?.name || "Metaship"),
      branding: computed(() => session.tenant?.branding ?? null),
    };
  },
});
</script>

<style scoped>
.csp__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.csp__section {
  font-size: 0.78rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sh-text-60);
  margin: 6px 0 -4px;
}
.csp__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 480px) {
  .csp__row {
    grid-template-columns: 1fr;
  }
}
.csp__submit {
  margin-top: 4px;
  font-weight: 650;
}
.csp__foot {
  font-size: 0.72rem;
  color: var(--sh-text-60);
  margin: 0;
  text-align: center;
  line-height: 1.5;
}
.csp__switch {
  font-size: 12px;
  color: var(--sh-text-60);
}
.csp__sent {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.08);
  margin-bottom: 14px;
}
.csp__sent p {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.55;
}
.csp__back {
  font-weight: 600;
}
.csp__trap {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
